'use client';

import * as React from 'react';
import { X, type LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

export type MobilePanelProps = {
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  bodyClassName?: string;
  /**
   * When provided, renders an X button on the right side of the header that
   * calls this handler. Always prefer this over relying on a parent dialog's
   * auto-injected close button — keeps positioning predictable.
   */
  onClose?: () => void;
};

export function MobilePanel({
  title,
  children,
  footer,
  className,
  bodyClassName,
  onClose,
}: MobilePanelProps) {
  return (
    <div className={cn('flex h-full min-h-0 flex-col bg-background', className)}>
      <div className="h-1 w-full bg-[var(--burgundy-core)]" />
      <div className="relative flex h-12 items-center border-b border-border px-4 pr-12">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-foreground">
          {title}
        </p>
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className={cn(
              'absolute right-2 top-1/2 -translate-y-1/2',
              'inline-flex h-8 w-8 items-center justify-center',
              'border border-transparent bg-transparent text-muted-foreground',
              'transition-all duration-[140ms]',
              'hover:bg-muted/40 hover:text-foreground',
              'focus-visible:outline-none focus-visible:border-primary',
              'focus-visible:ring-[3px] focus-visible:ring-primary/10 focus-visible:ring-offset-0',
            )}
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>
      <div
        className={cn(
          'min-h-0 flex-1 overflow-y-auto overscroll-contain bg-card px-2 pb-6 pt-4 sm:px-4',
          bodyClassName,
        )}
      >
        {children}
      </div>
      {footer ? (
        <div className="border-t border-border bg-card px-4 py-4">{footer}</div>
      ) : null}
    </div>
  );
}

type MobilePanelActionTone = 'default' | 'active';

export type MobilePanelActionButtonProps =
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    icon?: LucideIcon;
    tone?: MobilePanelActionTone;
    trailing?: React.ReactNode;
  };

export function MobilePanelActionButton({
  icon: Icon,
  tone = 'default',
  trailing,
  className,
  children,
  ...props
}: MobilePanelActionButtonProps) {
  return (
    <button
      type="button"
      className={cn(
        'flex h-12 w-full items-center gap-3 border px-3.5 text-left transition-colors',
        tone === 'active'
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border bg-background text-foreground hover:border-primary/60',
        className,
      )}
      {...props}
    >
      {Icon ? <Icon className="h-4 w-4 shrink-0" /> : null}
      <span className="min-w-0 flex-1 truncate text-sm font-bold tracking-tight">
        {children}
      </span>
      {trailing ? <span className="shrink-0">{trailing}</span> : null}
    </button>
  );
}

export type MobilePanelSectionLabelProps = {
  children: React.ReactNode;
  className?: string;
};

export function MobilePanelSectionLabel({
  children,
  className,
}: MobilePanelSectionLabelProps) {
  return (
    <p
      className={cn(
        'px-1 pt-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-foreground/65',
        className,
      )}
    >
      {children}
    </p>
  );
}
